// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ConvexSchema
 * @package @geenius/analytics/convex
 * @description Packaged Convex schema definitions for analytics events, page
 * views, funnels, cohorts, user identity traits, and session replay artifacts.
 * Consumers spread `analyticsTables` into their application schema.
 */

import { defineSchema, defineTable } from "convex/server";
import { v } from "convex/values";

const jsonPrimitive = v.union(v.null(), v.boolean(), v.number(), v.string());

/** Convex validator for JSON-safe analytics property records. */
export const jsonRecordSchema = v.record(v.string(), jsonPrimitive);

/** Convex validator for captured browser and device metadata. */
export const deviceInfoSchema = v.object({
  browser: v.string(),
  os: v.string(),
  device: v.union(
    v.literal("desktop"),
    v.literal("tablet"),
    v.literal("mobile"),
  ),
  screenWidth: v.number(),
  screenHeight: v.number(),
  language: v.string(),
});

/** Convex validator for persisted analytics event rows. */
export const analyticsEventSchema = v.object({
  id: v.string(),
  sessionId: v.string(),
  name: v.string(),
  type: v.string(),
  properties: v.optional(jsonRecordSchema),
  url: v.string(),
  referrer: v.string(),
  userId: v.optional(v.string()),
  traits: v.optional(jsonRecordSchema),
  device: deviceInfoSchema,
  timestamp: v.number(),
});

/** Convex validator for persisted analytics page-view rows. */
export const pageViewSchema = v.object({
  url: v.string(),
  title: v.string(),
  referrer: v.string(),
  properties: v.optional(jsonRecordSchema),
  duration: v.optional(v.number()),
  sessionId: v.string(),
  userId: v.optional(v.string()),
  timestamp: v.number(),
});

/** Convex validator for funnel-step definitions stored alongside funnel analyses. */
export const funnelStepSchema = v.object({
  name: v.string(),
  event: v.string(),
  properties: v.optional(jsonRecordSchema),
});

/** Convex validator for evaluated funnel-step output. */
export const funnelStepResultSchema = v.object({
  name: v.string(),
  event: v.string(),
  count: v.number(),
  dropoffRate: v.number(),
  conversionRate: v.number(),
});

/** Convex validator for complete funnel-analysis records. */
export const funnelResultSchema = v.object({
  name: v.string(),
  steps: v.array(funnelStepResultSchema),
  totalEntries: v.number(),
  totalConversions: v.number(),
  overallConversionRate: v.number(),
  analyzedAt: v.number(),
});

/** Convex validator for individual cohort-retention data points. */
export const cohortRetentionPointSchema = v.object({
  day: v.number(),
  retainedUsers: v.number(),
  retentionRate: v.number(),
});

/** Convex validator for named cohort buckets and their retention curves. */
export const cohortBucketSchema = v.object({
  cohort: v.string(),
  size: v.number(),
  retained: v.array(cohortRetentionPointSchema),
  lastActivity: v.number(),
});

/** Convex validator for packaged cohort-analysis output. */
export const cohortAnalysisSchema = v.object({
  name: v.string(),
  cohortType: v.union(v.literal("user"), v.literal("session")),
  intervals: v.array(v.number()),
  cohorts: v.array(cohortBucketSchema),
  generatedAt: v.number(),
});

/** Convex validator for a single normalized session-replay frame. */
export const sessionReplayFrameSchema = v.object({
  id: v.string(),
  kind: v.union(v.literal("event"), v.literal("page_view")),
  label: v.string(),
  url: v.string(),
  timestamp: v.number(),
  deltaMs: v.number(),
  sessionId: v.string(),
  userId: v.optional(v.string()),
  properties: v.optional(jsonRecordSchema),
});

/** Convex validator for a complete normalized session-replay snapshot. */
export const sessionReplaySnapshotSchema = v.object({
  sessionId: v.string(),
  startedAt: v.number(),
  endedAt: v.number(),
  duration: v.number(),
  frames: v.array(sessionReplayFrameSchema),
  activePages: v.array(v.string()),
  userId: v.optional(v.string()),
});

/** Convex validator for aggregated top-page rows. */
export const topPageSchema = v.object({
  url: v.string(),
  views: v.number(),
});

/** Convex validator for aggregated device-breakdown output. */
export const deviceBreakdownSchema = v.object({
  desktop: v.number(),
  tablet: v.number(),
  mobile: v.number(),
});

/** Convex validator for packaged realtime analytics snapshots. */
export const realtimeStatsSchema = v.object({
  activeVisitors: v.number(),
  currentPageViews: v.number(),
  activeSessions: v.number(),
  eventsPerSecond: v.number(),
  conversions: v.number(),
  conversionRate: v.number(),
  deviceBreakdown: deviceBreakdownSchema,
  topActivePages: v.array(
    v.object({
      url: v.string(),
      visitors: v.number(),
    }),
  ),
  lastUpdated: v.number(),
});

/** Named analytics tables exported for consumer schema composition. */
export const analyticsTables = {
  analytics_events: defineTable({
    id: v.string(),
    sessionId: v.string(),
    name: v.string(),
    type: v.string(),
    properties: v.optional(jsonRecordSchema),
    url: v.string(),
    referrer: v.string(),
    userId: v.optional(v.string()),
    traits: v.optional(jsonRecordSchema),
    device: deviceInfoSchema,
    timestamp: v.number(),
  })
    .index("by_id", ["id"])
    .index("by_sessionId", ["sessionId"])
    .index("by_userId", ["userId"])
    .index("by_timestamp", ["timestamp"])
    .index("by_name", ["name"]),

  analytics_pageviews: defineTable({
    url: v.string(),
    title: v.string(),
    referrer: v.string(),
    properties: v.optional(jsonRecordSchema),
    duration: v.optional(v.number()),
    sessionId: v.string(),
    userId: v.optional(v.string()),
    timestamp: v.number(),
  })
    .index("by_timestamp", ["timestamp"])
    .index("by_url", ["url"])
    .index("by_userId", ["userId"])
    .index("by_sessionId", ["sessionId"]),

  analytics_funnels: defineTable({
    name: v.string(),
    steps: v.array(funnelStepSchema),
    results: v.array(funnelStepResultSchema),
    totalEntries: v.number(),
    totalConversions: v.number(),
    overallConversionRate: v.number(),
    analyzedAt: v.number(),
  }).index("by_name", ["name"]),

  analytics_users: defineTable({
    userId: v.string(),
    traits: jsonRecordSchema,
    firstSeen: v.number(),
    lastSeen: v.number(),
  }).index("by_userId", ["userId"]),

  analytics_cohorts: defineTable({
    name: v.string(),
    cohortType: v.union(v.literal("user"), v.literal("session")),
    intervals: v.array(v.number()),
    cohorts: v.array(cohortBucketSchema),
    generatedAt: v.number(),
  })
    .index("by_name", ["name"])
    .index("by_generatedAt", ["generatedAt"]),

  analytics_session_replays: defineTable({
    sessionId: v.string(),
    startedAt: v.number(),
    endedAt: v.number(),
    duration: v.number(),
    frames: v.array(sessionReplayFrameSchema),
    activePages: v.array(v.string()),
    userId: v.optional(v.string()),
  })
    .index("by_sessionId", ["sessionId"])
    .index("by_endedAt", ["endedAt"]),
};

/**
 * Default Convex schema composed from the analytics package tables.
 *
 * @returns The schema definition consumed by generated Convex types and runtime helpers.
 */
const analyticsSchema = defineSchema(analyticsTables);

export default analyticsSchema;
